// frontend/src/services/api.js
// Complete API Service Layer for HOTWIFI v13.1.0 - UNIFIED AUDIT LOGS + WIFI PASSWORD VIEW

import axios from 'axios';
import { inkeyApi } from './inkeyApi';
import { clearAuthStorage, forceLogoutToLogin } from './sessionManager';

if (import.meta.env.PROD) {
  // Keep browser console clean in production: only errors.
  console.log = () => {};
  console.info = () => {};
  console.debug = () => {};
  console.warn = () => {};
}

// ============================================
// CONFIGURATION & CONSTANTS
// ============================================
const API_URL = import.meta.env.VITE_API_URL || '/api';
const API_TIMEOUT = 30000; // 30 seconds
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 1000;

const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  SERVICE_UNAVAILABLE: 503,
};

const ERROR_TYPES = {
  NETWORK: 'NETWORK_ERROR',
  AUTHENTICATION: 'AUTH_ERROR',
  AUTHORIZATION: 'PERMISSION_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  SERVER: 'SERVER_ERROR',
  TIMEOUT: 'TIMEOUT_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
};

// ============================================
// AXIOS INSTANCE CONFIGURATION
// ============================================
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
  withCredentials: true,
  timeout: API_TIMEOUT,
});

// ============================================
// REQUEST INTERCEPTOR
// ============================================
apiClient.interceptors.request.use(
  (config) => {
    // Add Authorization token - try multiple storage keys
    const token = localStorage.getItem('token') || localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      if (import.meta.env.DEV) {
        console.log('[API] Using token for request:', config.url, 'Token length:', token.length);
      }
    }

    //  CRITICAL: Add Branch ID header if available
    const selectedBranchStr = localStorage.getItem('selectedBranch');
    const selectedTenantStr = localStorage.getItem('selectedTenant');
    
    // Get tenant ID first
    let currentTenantId = null;
    if (selectedTenantStr) {
      try {
        const selectedTenant = JSON.parse(selectedTenantStr);
        currentTenantId = selectedTenant?._id || selectedTenant?.id;
      } catch (error) {
        console.error('Error parsing selectedTenant from localStorage:', error);
      }
    }
    
    // Only add branch header if branch is valid for current tenant context
    if (selectedBranchStr) {
      try {
        const selectedBranch = JSON.parse(selectedBranchStr);
        const branchId = selectedBranch?._id || selectedBranch?.id;
        
        if (branchId) {
          let addBranch = true;
          
          // If super admin has selected a tenant, validate branch belongs to that tenant
          if (currentTenantId && selectedBranch.tenant) {
            const branchTenantId = selectedBranch.tenant?._id || selectedBranch.tenant;
            if (branchTenantId && branchTenantId !== currentTenantId) {
              // Branch doesn't belong to selected tenant, skip it
              addBranch = false;
              if (import.meta.env.DEV) {
                console.warn('[API] Branch tenant mismatch - skipping branch header', {
                  branchId,
                  branchTenant: branchTenantId,
                  selectedTenant: currentTenantId
                });
              }
            }
          }
          
          if (addBranch) {
            config.headers['X-Branch-ID'] = branchId;
            if (import.meta.env.DEV) {
              console.log('[API] Using branch', branchId, 'for', config.url);
            }
          }
        } else if (import.meta.env.DEV) {
          console.warn('[API] selectedBranch found but no _id/id:', selectedBranch);
        }
      } catch (error) {
        console.error('Error parsing selectedBranch from localStorage:', error);
      }
    }

    //  NEW: Add Tenant ID header for super admin context switching
    if (currentTenantId) {
      config.headers['X-Tenant-Id'] = currentTenantId;
      if (import.meta.env.DEV) {
        console.log('[API] Using tenant', currentTenantId, 'for', config.url);
      }
    }

    // Add request metadata for logging
    config.metadata = { startTime: new Date() };

    // Development logging
    if (import.meta.env.DEV) {
      console.log(
        `[API Request] ${config.method?.toUpperCase()} ${config.url}`,
        {
          params: config.params,
          data: config.data,
          headers: config.headers,
        }
      );
    }

    return config;
  },
  (error) => {
    console.error('[API Request Error]:', error);
    return Promise.reject(error);
  }
);

// ============================================
// RESPONSE INTERCEPTOR
// ============================================
apiClient.interceptors.response.use(
  (response) => {
    const duration = new Date() - response.config.metadata.startTime;
    if (import.meta.env.DEV) {
      console.log(
        `[API Response] ${response.config.method?.toUpperCase()} ${response.config.url} - ${duration}ms`,
        response.data
      );
    }
    // Return the full response object
    return response;
  },
  async (error) => {
    if (!error.response) {
      console.error('[API Network Error]:', error);
      return Promise.reject({
        type: ERROR_TYPES.NETWORK,
        message: 'Network error. Please check your internet connection.',
        isNetworkError: true,
        originalError: error,
      });
    }

    if (error.response.status === HTTP_STATUS.UNAUTHORIZED) {
      // Check if this is a login attempt failure (not a session expiry)
      const isLoginEndpoint = error.config.url?.includes('/auth/login');
      
      if (isLoginEndpoint) {
        // For login failures, return the actual error message from backend
        return Promise.reject({
          type: ERROR_TYPES.AUTHENTICATION,
          message: error.response.data?.error || 'Incorrect username or password',
          status: HTTP_STATUS.UNAUTHORIZED,
          code: error.response.data?.code,
        });
      }

      // For other 401 errors (expired sessions), clear auth and redirect
      console.error('[API Auth Error] - Redirecting to login');
      await forceLogoutToLogin({
        onBeforeRedirect: async () => {
          try {
            const { disconnectSocket } = await import('./socket');
            disconnectSocket();
          } catch (e) {
            console.warn('Could not disconnect socket:', e);
          }
        }
      });

      return Promise.reject({
        type: ERROR_TYPES.AUTHENTICATION,
        message: 'Session expired. Please login again.',
        status: HTTP_STATUS.UNAUTHORIZED,
      });
    }

    if (error.response.status === HTTP_STATUS.FORBIDDEN) {
      return Promise.reject({
        type: ERROR_TYPES.AUTHORIZATION,
        message: 'You do not have permission to perform this action.',
        status: HTTP_STATUS.FORBIDDEN,
      });
    }

    if (
      error.response.status === HTTP_STATUS.BAD_REQUEST ||
      error.response.status === HTTP_STATUS.UNPROCESSABLE_ENTITY
    ) {
      return Promise.reject({
        type: ERROR_TYPES.VALIDATION,
        message: error.response.data.message || 'Validation error',
        errors: error.response.data.errors || [],
        status: error.response.status,
      });
    }

    if (error.response.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR) {
      return Promise.reject({
        type: ERROR_TYPES.SERVER,
        message: 'Server error. Please try again later.',
        status: error.response.status,
      });
    }

    if (import.meta.env.DEV) {
      console.error('[API Error Response]:', {
        status: error.response.status,
        data: error.response.data,
        url: error.config?.url,
      });
    }

    return Promise.reject({
      type: ERROR_TYPES.UNKNOWN,
      message: error.response.data?.message || error.response.data?.error || 'An unexpected error occurred',
      status: error.response.status,
      data: error.response.data,
    });
  }
);

// ============================================
// AUTHENTICATION API
// ============================================
export const authAPI = {
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      try {
        const { initializeSocket } = await import('./socket');
        initializeSocket();
      } catch (e) {
        console.warn('Could not initialize socket:', e);
      }
    }
    return response.data;
  },

  verify: async () => {
    const response = await apiClient.get('/auth/verify');
    return response.data;
  },

  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      clearAuthStorage();

      try {
        const { disconnectSocket } = await import('./socket');
        disconnectSocket();
      } catch (e) {
        console.warn('Could not disconnect socket:', e);
      }
    }
  },

  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  refreshToken: async () => {
    const response = await apiClient.post('/auth/refresh-token');
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  refresh: async (payload = {}) => {
    const response = await apiClient.post('/auth/refresh', payload);
    return response.data;
  },

  changePassword: async (passwordData) => {
    const response = await apiClient.post('/auth/change-password', passwordData);
    return response.data;
  },

  requestPasswordReset: async (payload) => {
    const response = await apiClient.post('/auth/forgot-password', payload);
    return response.data;
  },

  verifyPasswordResetOtp: async (payload) => {
    const response = await apiClient.post('/auth/forgot-password/verify', payload);
    return response.data;
  },

  resetPasswordWithOtp: async (payload) => {
    const response = await apiClient.post('/auth/forgot-password/reset', payload);
    return response.data;
  },

  resetPassword: async (token, newPassword) => {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      newPassword,
    });
    return response.data;
  },

  verifyEmail: async (token) => {
    const response = await apiClient.post('/auth/verify-email', { token });
    return response;
  },

  resendVerificationEmail: async (email) => {
    const response = await apiClient.post('/auth/resend-verification', {
      email,
    });
    return response.data;
  },
};

// ============================================
// ROOMS API
// ============================================
export const roomsAPI = {
  getAll: async (params = {}, config = {}) => {
    const response = await apiClient.get('/rooms', { params, ...config });
    return response.data;
  },

  getById: async (roomId) => {
    const response = await apiClient.get(`/rooms/${roomId}`);
    return response.data;
  },

  // 🆕 NEW v13.1.0: Get WiFi credentials with audit logging
  getWiFiCredentials: async (roomId) => {
    const response = await apiClient.get(`/rooms/${roomId}/wifi`);
    return response.data;
  },

  reconcilePassword: async (roomId) => {
    const response = await apiClient.post(`/rooms/${roomId}/reconcile-password`);
    return response.data;
  },

  sendWifiPasswordSms: async (roomId, payload = {}) => {
    const response = await apiClient.post(`/rooms/${roomId}/send-wifi-sms`, payload);
    return response.data;
  },

  create: async (roomData) => {
    const response = await apiClient.post('/rooms', roomData);
    return response.data;
  },

  update: async (roomId, roomData) => {
    const response = await apiClient.put(`/rooms/${roomId}`, roomData);
    return response.data;
  },

  delete: async (roomId) => {
    const response = await apiClient.delete(`/rooms/${roomId}`);
    return response.data;
  },

  batchUpdate: async (updates) => {
    const response = await apiClient.post('/rooms/batch-update', { updates });
    return response.data;
  },

  //  UPDATED v11.0.0: Removed maxUplink, maxDownlink, maxClients from check-in
  checkIn: async (checkInData) => {
    try {
      const payload = {
        roomNumber: checkInData.roomNumber,
        accommodationDays: checkInData.accommodationDays || 1,
        checkInDate: checkInData.checkInDate || new Date().toISOString(),
        guestLanguage: checkInData.guestLanguage || 'EN',
        vipStatus: checkInData.vipStatus || 'N',
      };

      // Only include optional fields when non-empty
      if (checkInData.email) payload.email = checkInData.email;
      if (checkInData.phone) payload.phone = checkInData.phone;
      if (checkInData.reservationId || checkInData.guestId) {
        payload.reservationId = checkInData.reservationId || checkInData.guestId;
      }

      //  REMOVED: maxUplink, maxDownlink, maxClients parameters

      // Support custom code mode
      if (checkInData.useCustomCode && checkInData.customCode) {
        if (!checkInData.customCode || !checkInData.customCode.trim()) {
          throw new Error('Custom code is required');
        }

        const guestName = (checkInData.guestName || '').trim();
        const fallbackGuestName = guestName || `Guest ${checkInData.roomNumber || ''}`.trim() || 'Guest';

        payload.guestName = fallbackGuestName;
        payload.customCode = checkInData.customCode.trim();
        payload.useCustomCode = true;
        const nameParts = fallbackGuestName.split(' ').filter(Boolean);
        payload.firstName = nameParts[0] || '';
        payload.lastName = nameParts.slice(1).join(' ') || String(checkInData.roomNumber || '').trim() || 'Guest';
      } else {
        // Traditional mode - split name
        const nameParts = (checkInData.guestName || '').trim().split(' ');
        payload.firstName = nameParts[0] || '';
        payload.lastName = nameParts.slice(1).join(' ') || nameParts[0] || 'Guest';
        if (checkInData.guestName?.trim()) {
          payload.guestName = checkInData.guestName.trim();
        }
      }

      console.log(' Check-in payload being sent:', payload);
      const response = await apiClient.post('/rooms/checkin', payload);
      return response.data;
    } catch (error) {
      console.error(' Check-in error:', error.response?.data || error.message);
      throw error;
    }
  },

  checkOut: async (checkOutData) => {
    const response = await apiClient.post('/rooms/checkout', checkOutData);
    return response.data;
  },

  //  UPDATED v11.0.0: Removed maxUplink, maxDownlink, maxClients from update
  updateGuestInfo: async (updateData) => {
    const payload = { ...updateData };
    //  REMOVED: maxUplink, maxDownlink, maxClients parameters
    const response = await apiClient.post('/rooms/update-guest', payload);
    return response.data;
  },

  getStatistics: async (branchId = null) => {
    const params = branchId ? { branchId } : {};
    const response = await apiClient.get('/rooms/statistics', { params });
    return response.data;
  },

  getHistory: async (roomId) => {
    const response = await apiClient.get(`/rooms/${roomId}/history`);
    return response.data;
  },

  updateStatus: async (roomId, status) => {
    const response = await apiClient.patch(`/rooms/${roomId}/status`, {
      status,
    });
    return response.data;
  },

  syncFromFIAS: async () => {
    const response = await apiClient.post('/rooms/sync-fias');
    return response.data;
  },

  getAvailable: async (params) => {
    const response = await apiClient.get('/rooms/available', { params });
    return response.data;
  },

  getOccupied: async (branchId = null) => {
    const params = { status: 'occupied' };
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get('/rooms', { params });
    return response.data;
  },

  bulkCreate: async (rooms) => {
    const response = await apiClient.post('/rooms/bulk-create', { rooms });
    return response.data;
  },

  bulkDelete: async (roomIds) => {
    const response = await apiClient.post('/rooms/bulk-delete', { roomIds });
    return response.data;
  },
};

// ============================================
// DEVICES API
// ============================================
export const devicesAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/devices', { params });
    return response.data;
  },

  getById: async (deviceId) => {
    const response = await apiClient.get(`/devices/${deviceId}`);
    return response.data;
  },

  create: async (deviceData) => {
    const response = await apiClient.post('/devices', deviceData);
    return response.data;
  },

  update: async (deviceId, deviceData) => {
    const response = await apiClient.put(`/devices/${deviceId}`, deviceData);
    return response.data;
  },

  delete: async (deviceId) => {
    const response = await apiClient.delete(`/devices/${deviceId}`);
    return response.data;
  },

  linkToRoom: async (deviceId, roomId) => {
    const response = await apiClient.post(`/devices/${deviceId}/link-room`, {
      roomId,
    });
    return response.data;
  },

  unlinkFromRoom: async (deviceId) => {
    const response = await apiClient.post(`/devices/${deviceId}/unlink-room`);
    return response.data;
  },

  getStatistics: async () => {
    const response = await apiClient.get('/devices/statistics');
    return response.data;
  },

  testConnection: async (deviceId) => {
    const response = await apiClient.post(
      `/devices/${deviceId}/test-connection`
    );
    return response.data;
  },

  startStop: async (deviceId, action) => {
    const response = await apiClient.post(`/devices/${deviceId}/${action}`);
    return response.data;
  },

  drSync: async (deviceId) => {
    const response = await apiClient.post(`/devices/${deviceId}/dr-sync`);
    return response.data;
  },

  reboot: async (deviceId) => {
    const response = await apiClient.post(`/devices/${deviceId}/reboot`);
    return response.data;
  },

  getLogs: async (deviceId, params = {}) => {
    const response = await apiClient.get(`/devices/${deviceId}/logs`, {
      params,
    });
    return response.data;
  },

  updateFirmware: async (deviceId, firmwareVersion) => {
    const response = await apiClient.post(
      `/devices/${deviceId}/update-firmware`,
      { firmwareVersion }
    );
    return response.data;
  },

  getStatus: async (deviceId) => {
    const response = await apiClient.get(`/devices/${deviceId}/status`);
    return response.data;
  },
};

// ============================================
// VOUCHERS API
// ============================================
export const vouchersAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/vouchers', { params });
    return response.data;
  },

  getById: async (voucherId) => {
    const response = await apiClient.get(`/vouchers/${voucherId}`);
    return response.data;
  },

  getStats: async () => {
    const response = await apiClient.get('/vouchers/stats');
    return response.data;
  },

  generate: async (generateData) => {
    const response = await apiClient.post('/vouchers/generate', generateData);
    return response.data;
  },

  getVoucherTypes: async (guestType = null) => {
    const params = {};
    if (guestType) params.guestType = guestType;
    const response = await apiClient.get('/vouchers/voucher-types', { params });
    return response.data;
  },

  getAvailableValidities: async (guestType = null) => {
    const params = {};
    if (guestType) params.guestType = guestType;
    const response = await apiClient.get('/vouchers/available-validities', {
      params,
    });
    return response.data;
  },

  quickIssue: async (issueData) => {
    const response = await apiClient.post('/vouchers/quick-issue', issueData);
    return response.data;
  },

  getRecentIssued: async (limit = 10) => {
    const response = await apiClient.get('/vouchers/recent-issued', {
      params: { limit },
    });
    return response.data;
  },

  createAddon: async (roomId, { quantity, deviceId, userGroupId }) => {
    const response = await apiClient.post(`/vouchers/addon/${roomId}`, {
      quantity,
      deviceId,
      userGroupId,
    });
    return response.data;
  },

  importVouchers: async (file, voucherType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('voucherType', voucherType);

    const response = await apiClient.post('/vouchers/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  downloadImportTemplate: async () => {
    const response = await apiClient.get('/vouchers/import/template', {
      responseType: 'blob',
    });
    return response.data;
  },

  issueVoucher: async (voucherId, issueData) => {
    const response = await apiClient.post(
      `/vouchers/${voucherId}/issue`,
      issueData
    );
    return response.data;
  },

  getUserGroups: async (deviceId) => {
    const response = await apiClient.get(
      `/vouchers/device/${deviceId}/user-groups`
    );
    return response.data;
  },

  getRoomAddons: async (roomId) => {
    const response = await apiClient.get(`/vouchers/room/${roomId}/addons`);
    return response.data;
  },

  delete: async (voucherId) => {
    const response = await apiClient.delete(`/vouchers/${voucherId}`);
    return response.data;
  },

  batchDelete: async (voucherIds) => {
    const response = await apiClient.post('/vouchers/batch-delete', {
      voucherIds,
    });
    return response.data;
  },

  activate: async (voucherId) => {
    const response = await apiClient.post(`/vouchers/${voucherId}/activate`);
    return response.data;
  },

  deactivate: async (voucherId) => {
    const response = await apiClient.post(`/vouchers/${voucherId}/deactivate`);
    return response.data;
  },

  getStatistics: async () => {
    const response = await apiClient.get('/vouchers/statistics');
    return response.data;
  },

  export: async (voucherIds, format = 'pdf') => {
    const response = await apiClient.post(
      '/vouchers/export',
      { voucherIds, format },
      { responseType: 'blob' }
    );
    return response.data;
  },

  print: async (voucherIds) => {
    const response = await apiClient.post('/vouchers/print', { voucherIds });
    return response.data;
  },

  redeem: async (code) => {
    const response = await apiClient.post('/vouchers/redeem', { code });
    return response.data;
  },

  use: async (voucherId, usageData) => {
    const response = await apiClient.post(`/vouchers/${voucherId}/use`, usageData);
    return response.data;
  },
};

// ============================================
// INKEY API - Re-exported from inkeyApi.js
// ============================================
export { inkeyApi };

// ============================================
// USERS API
// ============================================
export const usersAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  getById: async (userId) => {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },

  create: async (userData) => {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  update: async (userId, userData) => {
    const response = await apiClient.put(`/users/${userId}`, userData);
    return response.data;
  },

  delete: async (userId, permanent = false) => {
    const url = permanent ? `/users/${userId}?permanent=true` : `/users/${userId}`;
    const response = await apiClient.delete(url);
    return response.data;
  },

  updatePassword: async (userId, passwordData) => {
    const response = await apiClient.post(
      `/users/${userId}/change-password`,
      passwordData
    );
    return response.data;
  },

  toggleStatus: async (userId, isActive) => {
    const response = await apiClient.patch(`/users/${userId}/status`, {
      isActive,
    });
    return response.data;
  },

  assignBranches: async (userId, branchIds) => {
    const response = await apiClient.post(`/users/${userId}/assign-branches`, {
      branchIds,
    });
    return response.data;
  },

  getActivityLogs: async (userId, params = {}) => {
    const response = await apiClient.get(`/users/${userId}/activity`, {
      params,
    });
    return response.data;
  },

  updatePermissions: async (userId, permissions) => {
    const response = await apiClient.patch(`/users/${userId}/permissions`, {
      permissions,
    });
    return response.data;
  },

  getPassword: async (userId) => {
    const response = await apiClient.get(`/users/${userId}/password`);
    return response.data;
  },
};

// ============================================
// USER PAGE PERMISSIONS API
// ============================================
export const userPermissionsAPI = {
  getAvailablePages: async () => {
    const response = await apiClient.get('/user-permissions/available-pages');
    return response.data;
  },

  getUserPermissions: async (userId) => {
    const response = await apiClient.get(`/user-permissions/${userId}`);
    return response.data;
  },

  updatePagePermissions: async (userId, pageName, permissions) => {
    const response = await apiClient.put(
      `/user-permissions/${userId}/pages/${pageName}`,
      permissions
    );
    return response.data;
  },

  updateAllPermissions: async (userId, pages) => {
    const response = await apiClient.put(
      `/user-permissions/${userId}/bulk`,
      { pages }
    );
    return response.data;
  },

  disablePage: async (userId, pageName) => {
    const response = await apiClient.delete(
      `/user-permissions/${userId}/pages/${pageName}`
    );
    return response.data;
  },
};

// ============================================
// BRANCHES API
// ============================================
export const branchesAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/branches', { params });
    return response.data;
  },

  getById: async (branchId) => {
    const response = await apiClient.get(`/branches/${branchId}`);
    return response.data;
  },

  create: async (branchData) => {
    const response = await apiClient.post('/branches', branchData);
    return response.data;
  },

  update: async (branchId, branchData) => {
    const response = await apiClient.put(`/branches/${branchId}`, branchData);
    return response.data;
  },

  delete: async (branchId) => {
    const response = await apiClient.delete(`/branches/${branchId}`);
    return response.data;
  },

  getStatistics: async (branchId) => {
    const response = await apiClient.get(`/branches/${branchId}/statistics`);
    return response.data;
  },

  getUserBranches: async () => {
    const response = await apiClient.get('/branches/user-branches');
    return response.data;
  },
};

// ============================================
// TENANTS API (SUPER ADMIN)
// ============================================
export const tenantsAPI = {
  list: async () => {
    const response = await apiClient.get('/tenants');
    return response.data;
  },
  create: async (data) => {
    const response = await apiClient.post('/tenants', data);
    return response.data;
  },
  update: async (id, data) => {
    const response = await apiClient.put(`/tenants/${id}`, data);
    return response.data;
  },
  delete: async (id) => {
    const response = await apiClient.delete(`/tenants/${id}`);
    return response.data;
  }
};

// ============================================
// DASHBOARD API
// ============================================
export const dashboardAPI = {
  getStatistics: async (branchId = null, dateRange = null) => {
    const params = {};
    if (branchId) params.branchId = branchId;
    if (dateRange) {
      params.startDate = dateRange.startDate;
      params.endDate = dateRange.endDate;
    }
    const response = await apiClient.get('/dashboard/statistics', { params });
    return response.data;
  },

  getRecentActivities: async (limit = 10) => {
    const response = await apiClient.get('/dashboard/activities', {
      params: { limit },
    });
    return response.data;
  },

  getSystemHealth: async () => {
    const response = await apiClient.get('/dashboard/health');
    return response.data;
  },

  getAnalytics: async (type, dateRange) => {
    const response = await apiClient.get('/dashboard/analytics', {
      params: { type, ...dateRange },
    });
    return response.data;
  },

  getOccupancyTrends: async (branchId = null, period = '7d') => {
    const params = { period };
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get('/dashboard/occupancy-trends', {
      params,
    });
    return response.data;
  },
};

// ============================================
// SETTINGS API
// ============================================
export const settingsAPI = {
  getAll: async () => {
    const response = await apiClient.get('/settings');
    return response.data;
  },

  getCategory: async (category) => {
    const response = await apiClient.get(`/settings/${category}`);
    return response.data;
  },

  update: async (category, settingsData) => {
    const response = await apiClient.put(`/settings/${category}`, settingsData);
    return response.data;
  },

  resetToDefault: async (category) => {
    const response = await apiClient.post(`/settings/${category}/reset`);
    return response.data;
  },

  getSystem: async () => {
    const response = await apiClient.get('/settings/system');
    return response.data;
  },

  updateSystem: async (settingsData) => {
    const response = await apiClient.put('/settings/system', settingsData);
    return response.data;
  },

  getFIAS: async () => {
    const response = await apiClient.get('/settings/fias');
    return response.data;
  },

  updateFIAS: async (settingsData) => {
    const response = await apiClient.put('/settings/fias', settingsData);
    return response.data;
  },

  testFIAS: async () => {
    const response = await apiClient.post('/settings/fias/test-connection');
    return response.data;
  },

  getRuijie: async () => {
    const response = await apiClient.get('/settings/ruijie');
    return response.data;
  },

  updateRuijie: async (settingsData) => {
    const response = await apiClient.put('/settings/ruijie', settingsData);
    return response.data;
  },

  testRuijie: async () => {
    const response = await apiClient.post('/settings/ruijie/test-connection');
    return response.data;
  },

  getEmail: async () => {
    const response = await apiClient.get('/settings/email');
    return response.data;
  },

  updateEmail: async (settingsData) => {
    const response = await apiClient.put('/settings/email', settingsData);
    return response.data;
  },

  testEmail: async (testEmail) => {
    const response = await apiClient.post('/settings/email/test', { testEmail });
    return response.data;
  },

  getBackup: async () => {
    const response = await apiClient.get('/settings/backup');
    return response.data;
  },

  updateBackup: async (settingsData) => {
    const response = await apiClient.put('/settings/backup', settingsData);
    return response.data;
  },

  triggerBackup: async () => {
    const response = await apiClient.post('/settings/backup/trigger');
    return response.data;
  },

  getCustomization: async () => {
    const response = await apiClient.get('/settings/customization');
    return response.data;
  },

  updateCustomization: async (settingsData) => {
    const response = await apiClient.put(
      '/settings/customization',
      settingsData
    );
    return response.data;
  },

  getSessionTimeout: async (branchId) => {
    const params = branchId ? { branchId } : {};
    const response = await apiClient.get('/settings/security/session-timeout', { params });
    return response.data;
  },

  updateSessionTimeout: async ({ mode, reason, branchId }) => {
    const payload = { mode };
    if (reason) payload.reason = reason;
    if (branchId) payload.branchId = branchId;
    const response = await apiClient.put('/settings/security/session-timeout', payload);
    return response.data;
  },

  getStatus: async (branchId) => {
    const params = branchId ? { branchId } : {};
    const response = await apiClient.get('/settings/status', { params });
    return response.data;
  },

  getHealth: async () => {
    const response = await apiClient.get('/settings/health');
    return response.data;
  },

  updateIntegration: async (type, data) => {
    const response = await apiClient.put(`/settings/${type}`, data);
    return response.data;
  },

  // Integration toggle methods
  ruijie: {
    toggle: async (enabled, branchId, reason) => {
      const data = { enabled, reason: reason || '' };
      if (branchId) data.branchId = branchId;
      const response = await apiClient.post('/settings/ruijie/toggle', data);
      return response.data;
    },
  },

  lysora: {
    toggle: async (enabled, branchId, reason) => {
      const data = { enabled, reason: reason || '' };
      if (branchId) data.branchId = branchId;
      const response = await apiClient.post('/settings/lysora/toggle', data);
      return response.data;
    },
  },

  fias: {
    toggle: async (enabled, branchId, reason) => {
      const data = { enabled, reason: reason || '' };
      if (branchId) data.branchId = branchId;
      const response = await apiClient.post('/settings/fias/toggle', data);
      return response.data;
    },
  },

  inkey: {
    toggle: async (enabled, branchId, reason) => {
      const data = { enabled, reason: reason || '' };
      if (branchId) data.branchId = branchId;
      const response = await apiClient.post('/settings/inkey/toggle', data);
      return response.data;
    },
  },

  wincloud: {
    toggle: async (enabled, branchId, reason) => {
      const data = { enabled, reason: reason || '' };
      if (branchId) data.branchId = branchId;
      const response = await apiClient.post('/settings/wincloud/toggle', data);
      return response.data;
    },
  },

  hotelkey: {
    toggle: async (enabled, branchId, reason) => {
      const data = { enabled, reason: reason || '' };
      if (branchId) data.branchId = branchId;
      const response = await apiClient.post('/settings/hotelkey/toggle', data);
      return response.data;
    },
  },

  smtp: {
    get: async (branchId) => {
      const params = branchId ? { branchId } : {};
      const response = await apiClient.get('/settings/smtp', { params });
      return response.data;
    },

    update: async (payload) => {
      const response = await apiClient.put('/settings/smtp', payload);
      return response.data;
    },

    test: async ({ branchId, testEmail }) => {
      const response = await apiClient.post('/settings/smtp/test', { branchId, testEmail });
      return response.data;
    },
    // Branch SMS settings (shared endpoint)
    getSms: async (branchId) => {
      const params = branchId ? { branchId } : {};
      const response = await apiClient.get('/settings/sms', { params });
      return response.data;
    },
    updateSms: async (payload) => {
      const response = await apiClient.put('/settings/sms', payload);
      return response.data;
    },
  },

  partnerApi: {
    requestAccess: async ({ branchId }) => {
      const response = await apiClient.post('/settings/partner-api/request', { branchId });
      return response.data;
    },

    getRequestState: async (branchId) => {
      const params = branchId ? { branchId } : {};
      const response = await apiClient.get('/settings/partner-api/request', { params });
      return response.data;
    },

    getCredentials: async (branchId) => {
      const params = branchId ? { branchId } : {};
      const response = await apiClient.get('/settings/partner-api/credentials', { params });
      return response.data;
    },
  },
};

// ============================================
// SUPPORT API
// ============================================
export const supportAPI = {
  createTicket: async ({ title, description, category, priority, urgency, branchId, attachments = [] }) => {
    const payload = {
      title,
      description,
      category,
      priority,
      urgency,
      attachments,
    };
    if (branchId) payload.branchId = branchId;
    const response = await apiClient.post('/settings/support/tickets', payload);
    return response.data;
  },

  getMyTickets: async ({ status, branchId, tenantId, search, limit = 20, skip = 0 } = {}) => {
    const params = { limit, skip };
    if (status) params.status = status;
    if (branchId) params.branchId = branchId;
    if (tenantId) params.tenantId = tenantId;
    if (search) params.search = search;
    const response = await apiClient.get('/settings/support/tickets', { params });
    return response.data;
  },

  getTicketDetails: async (ticketId) => {
    const response = await apiClient.get(`/settings/support/tickets/${ticketId}`);
    return response.data;
  },

  updateTicketAsSuperAdmin: async (ticketId, payload) => {
    const response = await apiClient.put(`/super-admin/support/tickets/${ticketId}`, payload);
    return response.data;
  },
};

// ============================================
// LOGS API - ENHANCED v13.0.0 WITH UNIFIED AUDIT LOGS
// ============================================
export const logsAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/logs', { params });
    return response.data;
  },

  getSystem: async (params = {}) => {
    const response = await apiClient.get('/logs/system', { params });
    return response.data;
  },

  getActivity: async (params = {}) => {
    const response = await apiClient.get('/logs/activity', { params });
    return response.data;
  },

  getErrors: async (params = {}) => {
    const response = await apiClient.get('/logs/errors', { params });
    return response.data;
  },

  getFIAS: async (params = {}) => {
    const response = await apiClient.get('/logs/fias', { params });
    return response.data;
  },

  // NEW: Get room activity logs
  getRoomLogs: async (params = {}, config = {}) => {
    const response = await apiClient.get('/logs/rooms', { params, ...config });
    return response.data;
  },

  //  FIXED v13.0.0: Use unified endpoint with pre-formatted data
  getAuditLogs: async (params = {}, config = {}) => {
    const response = await apiClient.get('/logs/audit/unified', {
      params,
      ...config,
    });
    // Unified endpoint returns: { success, data: [...], pagination }
    return response.data;
  },

  clear: async (logType) => {
    const response = await apiClient.delete(`/logs/${logType}`);
    return response.data;
  },

  export: async (logType, format = 'csv') => {
    const response = await apiClient.get(`/logs/${logType}/export`, {
      params: { format },
      responseType: 'blob',
    });
    return response.data;
  },

  createAuditLog: async (auditData) => {
    try {
      const response = await apiClient.post('/logs/audit/create', auditData);
      return response.data;
    } catch (error) {
      // Silently fail for audit logging - don't disrupt user workflow
      console.warn('Failed to create audit log:', error);
      return { success: false };
    }
  },
};

// ============================================
// FIAS MONITOR API
// ============================================
export const fiasMonitorAPI = {
  getStatus: async () => {
    const response = await apiClient.get('/fias/status');
    return response.data;
  },

  getRecentMessages: async (limit = 50) => {
    const response = await apiClient.get('/fias/messages', {
      params: { limit },
    });
    return response.data;
  },

  getStatistics: async (dateRange = null) => {
    const params = dateRange ? { ...dateRange } : {};
    const response = await apiClient.get('/fias/statistics', { params });
    return response.data;
  },

  start: async () => {
    const response = await apiClient.post('/fias/start');
    return response.data;
  },

  stop: async () => {
    const response = await apiClient.post('/fias/stop');
    return response.data;
  },

  restart: async () => {
    const response = await apiClient.post('/fias/restart');
    return response.data;
  },

  clearQueue: async () => {
    const response = await apiClient.post('/fias/clear-queue');
    return response.data;
  },

  sendTestMessage: async (messageType, data) => {
    const response = await apiClient.post('/fias/test-message', {
      messageType,
      data,
    });
    return response.data;
  },
};

// ============================================
// RUIJIE API
// ============================================
export const ruijieAPI = {
  getStatus: async () => {
    const response = await apiClient.get('/ruijie/status');
    return response.data;
  },

  getAccounts: async (params = {}) => {
    const response = await apiClient.get('/ruijie/accounts', { params });
    return response.data;
  },

  createAccount: async (accountData) => {
    const response = await apiClient.post('/ruijie/accounts', accountData);
    return response.data;
  },

  deleteAccount: async (username) => {
    const response = await apiClient.delete(`/ruijie/accounts/${username}`);
    return response.data;
  },

  getAccountDetails: async (username) => {
    const response = await apiClient.get(`/ruijie/accounts/${username}`);
    return response.data;
  },

  getOnlineUsers: async () => {
    const response = await apiClient.get('/ruijie/online-users');
    return response.data;
  },

  kickUser: async (username) => {
    const response = await apiClient.post(`/ruijie/kick/${username}`);
    return response.data;
  },

  getAccessPoints: async () => {
    const response = await apiClient.get('/ruijie/access-points');
    return response.data;
  },

  getStatistics: async () => {
    const response = await apiClient.get('/ruijie/statistics');
    return response.data;
  },

  sync: async () => {
    const response = await apiClient.post('/ruijie/sync');
    return response.data;
  },
};

// ============================================
// REPORTS API
// ============================================
export const reportsAPI = {
  getOccupancy: async (dateRange, branchId = null) => {
    const params = { ...dateRange };
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get('/reports/occupancy', { params });
    return response.data;
  },

  getRevenue: async (dateRange, branchId = null) => {
    const params = { ...dateRange };
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get('/reports/revenue', { params });
    return response.data;
  },

  getGuests: async (dateRange, branchId = null) => {
    const params = { ...dateRange };
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get('/reports/guests', { params });
    return response.data;
  },

  getWiFiUsage: async (dateRange, branchId = null) => {
    const params = { ...dateRange };
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get('/reports/wifi-usage', { params });
    return response.data;
  },

  export: async (reportType, format, params) => {
    const response = await apiClient.post(
      `/reports/${reportType}/export`,
      { format, ...params },
      { responseType: 'blob' }
    );
    return response.data;
  },

  scheduleReport: async (scheduleData) => {
    const response = await apiClient.post('/reports/schedule', scheduleData);
    return response.data;
  },

  getScheduledReports: async () => {
    const response = await apiClient.get('/reports/scheduled');
    return response.data;
  },

  deleteScheduledReport: async (scheduleId) => {
    const response = await apiClient.delete(`/reports/scheduled/${scheduleId}`);
    return response.data;
  },
};

// ============================================
// NOTIFICATIONS API
// ============================================
export const notificationsAPI = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/notifications', { params });
    return response.data;
  },

  getUnreadCount: async () => {
    const response = await apiClient.get('/notifications/unread-count');
    return response.data;
  },

  markAsRead: async (notificationId) => {
    const response = await apiClient.patch(
      `/notifications/${notificationId}/read`
    );
    return response.data;
  },

  markAllAsRead: async () => {
    const response = await apiClient.patch('/notifications/read-all');
    return response.data;
  },

  delete: async (notificationId) => {
    const response = await apiClient.delete(`/notifications/${notificationId}`);
    return response.data;
  },

  clearAll: async () => {
    const response = await apiClient.delete('/notifications/clear-all');
    return response.data;
  },

  getSettings: async () => {
    const response = await apiClient.get('/notifications/settings');
    return response.data;
  },

  updateSettings: async (settingsData) => {
    const response = await apiClient.put('/notifications/settings', settingsData);
    return response.data;
  },
};

// ============================================
// ACCESS TOKENS API
// ============================================
export const accessTokensAPI = {
  // Create new access token
  create: async (tokenData) => {
    const response = await apiClient.post('/access-tokens', tokenData);
    return response.data;
  },

  // Get all tokens created by current user
  list: async (params = {}) => {
    const response = await apiClient.get('/access-tokens', { params });
    return response.data;
  },

  // Get single token details
  getById: async (id) => {
    const response = await apiClient.get(`/access-tokens/${id}`);
    return response.data;
  },

  // Revoke token
  revoke: async (id, reason) => {
    const response = await apiClient.delete(`/access-tokens/${id}`, {
      data: { reason }
    });
    return response.data;
  },

  // Validate token (for login)
  validate: async (token) => {
    const response = await apiClient.post('/access-tokens/validate', { token });
    return response.data;
  },

  // Login with token
  loginWithToken: async (token) => {
    const response = await apiClient.post('/auth/login/token', { token });
    return response.data;
  },

  // Generate token from template
  generateFromTemplate: async (data) => {
    const response = await apiClient.post('/access-tokens/from-template', data);
    return response.data;
  }
};

// ============================================
// PERMISSION TEMPLATES API
// ============================================
export const templatesAPI = {
  // List templates
  list: async (params = {}) => {
    const response = await apiClient.get('/templates', { params });
    return response.data;
  },

  // Get template by id
  getById: async (id) => {
    const response = await apiClient.get(`/templates/${id}`);
    return response.data;
  },

  // Create template
  create: async (data) => {
    const response = await apiClient.post('/templates', data);
    return response.data;
  },

  // Update template
  update: async (id, data) => {
    const response = await apiClient.put(`/templates/${id}`, data);
    return response.data;
  },

  // Delete template
  remove: async (id) => {
    const response = await apiClient.delete(`/templates/${id}`);
    return response.data;
  }
};

// ============================================
// FILE UPLOAD API
// ============================================
export const fileAPI = {
  upload: async (file, category = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);

    const response = await apiClient.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  uploadMultiple: async (files, category = 'general') => {
    const formData = new FormData();
    files.forEach((file) => formData.append('files', file));
    formData.append('category', category);

    const response = await apiClient.post('/files/upload-multiple', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  delete: async (fileId) => {
    const response = await apiClient.delete(`/files/${fileId}`);
    return response.data;
  },

  getDetails: async (fileId) => {
    const response = await apiClient.get(`/files/${fileId}`);
    return response.data;
  },

  download: async (fileId) => {
    const response = await apiClient.get(`/files/${fileId}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },
};

// ============================================
// SUPER ADMIN API
// ============================================
export const superAdminAPI = {
  // Global Configuration
  getGlobalSmtp: async () => {
    const response = await apiClient.get('/super-admin/config/global-smtp');
    return response.data;
  },

  updateGlobalSmtp: async (config) => {
    const response = await apiClient.put('/super-admin/config/global-smtp', config);
    return response.data;
  },

  getGlobalSms: async () => {
    const response = await apiClient.get('/super-admin/config/global-sms');
    return response.data;
  },

  updateGlobalSms: async (config) => {
    const response = await apiClient.put('/super-admin/config/global-sms', config);
    return response.data;
  },

  getSessionTimeout: async () => {
    const response = await apiClient.get('/super-admin/config/session-timeout');
    return response.data;
  },

  updateSessionTimeout: async (timeoutMin) => {
    const response = await apiClient.put('/super-admin/config/session-timeout', { minutes: timeoutMin });
    return response.data;
  },

  getBranchSessionTimeouts: async (tenantId) => {
    const response = await apiClient.get('/super-admin/config/branch-session-timeouts', {
      params: { tenantId }
    });
    return response.data;
  },

  updateBranchSessionTimeout: async (branchId, mode, tenantId) => {
    const response = await apiClient.put(`/super-admin/config/branch/${branchId}/session-timeout`, {
      mode,
      tenantId
    });
    return response.data;
  },

  // Global Configurations (Database-Driven)
  listGlobalConfigs: async (params = {}) => {
    const response = await apiClient.get('/super-admin/global-configs', { params });
    return response.data;
  },

  getGlobalConfig: async (key, includeSecret = false) => {
    const response = await apiClient.get(`/super-admin/global-configs/${key}`, {
      params: { includeSecret }
    });
    return response.data;
  },

  createGlobalConfig: async (config) => {
    const response = await apiClient.post('/super-admin/global-configs', config);
    return response.data;
  },

  updateGlobalConfig: async (key, config) => {
    const response = await apiClient.put(`/super-admin/global-configs/${key}`, config);
    return response.data;
  },

  deleteGlobalConfig: async (key) => {
    const response = await apiClient.delete(`/super-admin/global-configs/${key}`);
    return response.data;
  },

  toggleGlobalConfig: async (key) => {
    const response = await apiClient.patch(`/super-admin/global-configs/${key}/toggle`);
    return response.data;
  },

  getConfigCategories: async () => {
    const response = await apiClient.get('/super-admin/global-configs/categories/list');
    return response.data;
  },

  // Tenant Management
  blockTenant: async (tenantId, reason) => {
    const response = await apiClient.post(`/super-admin/tenant/${tenantId}/block`, { reason });
    return response.data;
  },

  unblockTenant: async (tenantId, reason) => {
    const response = await apiClient.post(`/super-admin/tenant/${tenantId}/unblock`, { reason });
    return response.data;
  },

  verifyTenant: async (tenantId, reason = '') => {
    const response = await apiClient.post(`/super-admin/tenant/${tenantId}/verify`, { reason });
    return response.data;
  },

  deleteTenant: async (tenantId, reason = '') => {
    const response = await apiClient.delete(`/super-admin/tenant/${tenantId}`, {
      data: { reason }
    });
    return response.data;
  },

  // User Management
  getUsers: async (tenantId, branchId = null) => {
    const params = { tenantId, limit: 500 };
    if (branchId) params.branchId = branchId;
    const response = await apiClient.get('/super-admin/users', { params });
    return response.data;
  },

  getUserSessions: async ({ tenantId = '', branchId = '', search = '', limit = 500 } = {}) => {
    const params = { limit };
    if (tenantId) params.tenantId = tenantId;
    if (branchId) params.branchId = branchId;
    if (search) params.search = search;
    const response = await apiClient.get('/super-admin/user-sessions', { params });
    return response.data;
  },

  kickoutUsers: async (query) => {
    const response = await apiClient.post('/super-admin/users/kickout', query);
    return response.data;
  },

  disconnectUserSession: async (userId, reason = 'Disconnected by super admin') => {
    const response = await apiClient.post('/super-admin/users/kickout', { userId, reason });
    return response.data;
  },

  overridePassword: async (userId, newPassword) => {
    const response = await apiClient.post(`/super-admin/user/${userId}/override-password`, {
      newPassword,
    });
    return response.data;
  },

  // Analytics
  getAllTenantsStats: async () => {
    const response = await apiClient.get('/super-admin/analytics/all-tenants');
    return response.data;
  },

  getTenantStats: async (tenantId) => {
    const response = await apiClient.get(`/super-admin/analytics/tenant/${tenantId}`);
    return response.data;
  },

  // Audit Logs
  getAuditLogs: async (params = {}) => {
    const response = await apiClient.get('/super-admin/audit-logs', { params });
    return response.data;
  },

  // Backups
  createFullBackup: async () => {
    const response = await apiClient.post('/super-admin/backup/full');
    return response.data;
  },

  createTenantBackup: async (tenantId, format = 'json') => {
    const response = await apiClient.post(`/super-admin/backup/tenant/${tenantId}`, { format });
    return response.data;
  },

  listBackups: async () => {
    const response = await apiClient.get('/super-admin/backup/list');
    return response.data;
  },

  downloadBackup: async (backupName) => {
    const response = await apiClient.get(`/super-admin/backup/download/${backupName}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  cleanupBackups: async (retentionDays = 30) => {
    const response = await apiClient.post('/super-admin/backup/cleanup', { retentionDays });
    return response.data;
  },

  listPartnerApiRequests: async (params = {}) => {
    const response = await apiClient.get('/super-admin/partner-api/requests', { params });
    return response.data;
  },

  approvePartnerApiRequest: async (requestId) => {
    const response = await apiClient.post(`/super-admin/partner-api/requests/${requestId}/approve`);
    return response.data;
  },

  rejectPartnerApiRequest: async (requestId) => {
    const response = await apiClient.post(`/super-admin/partner-api/requests/${requestId}/reject`);
    return response.data;
  },

  deletePartnerApiRequest: async (requestId) => {
    const response = await apiClient.delete(`/super-admin/partner-api/requests/${requestId}`);
    return response.data;
  },

  listSupportTickets: async (params = {}) => {
    const response = await apiClient.get('/super-admin/support/tickets', { params });
    return response.data;
  },

  updateSupportTicket: async (ticketId, payload) => {
    const response = await apiClient.put(`/super-admin/support/tickets/${ticketId}`, payload);
    return response.data;
  },
};

// ============================================
// PRIVACY (GDPR/DSAR) API
// ============================================
export const privacyAPI = {
  exportMyData: async () => {
    const response = await apiClient.get('/privacy/me/export');
    return response.data;
  },

  requestDeletion: async (reason = '') => {
    const response = await apiClient.post('/privacy/me/delete-request', { reason });
    return response.data;
  },

  listMyRequests: async () => {
    const response = await apiClient.get('/privacy/me/requests');
    return response.data;
  },

  listRequests: async (params = {}) => {
    const response = await apiClient.get('/privacy/requests', { params });
    return response.data;
  },

  reviewRequest: async (requestId, payload) => {
    const response = await apiClient.post(`/privacy/requests/${requestId}/review`, payload);
    return response.data;
  },

  executeRequest: async (requestId) => {
    const response = await apiClient.post(`/privacy/requests/${requestId}/execute`);
    return response.data;
  },
};

// ============================================
// SYSTEM MONITORING API
// ============================================
export const monitoringAPI = {
  getHealth: async () => {
    const response = await apiClient.get('/monitoring/health');
    return response.data;
  },

  getMetrics: async () => {
    const response = await apiClient.get('/monitoring/metrics');
    return response.data;
  },

  getCPU: async () => {
    const response = await apiClient.get('/monitoring/cpu');
    return response.data;
  },

  getMemory: async () => {
    const response = await apiClient.get('/monitoring/memory');
    return response.data;
  },

  getDisk: async () => {
    const response = await apiClient.get('/monitoring/disk');
    return response.data;
  },

  getNetwork: async () => {
    const response = await apiClient.get('/monitoring/network');
    return response.data;
  },

  getServices: async () => {
    const response = await apiClient.get('/monitoring/services');
    return response.data;
  },

  getErrors: async (params = {}) => {
    const response = await apiClient.get('/monitoring/errors', { params });
    return response.data;
  },

  getPerformance: async (timeRange = '1h') => {
    const response = await apiClient.get('/monitoring/performance', {
      params: { timeRange },
    });
    return response.data;
  },

  restartService: async (serviceName) => {
    const response = await apiClient.post(
      `/monitoring/services/${serviceName}/restart`
    );
    return response.data;
  },

  clearCache: async () => {
    const response = await apiClient.post('/monitoring/cache/clear');
    return response.data;
  },
};

// ============================================
// STANDALONE CHECK-IN GUEST FUNCTION - UPDATED v11.0.0
// ============================================
export const checkInGuest = async (checkInData) => {
  try {
    const payload = {
      roomNumber: checkInData.roomNumber,
      accommodationDays: checkInData.accommodationDays || 1,
      checkInDate: checkInData.checkInDate || new Date().toISOString(),
      guestLanguage: checkInData.guestLanguage || 'EN',
      vipStatus: checkInData.vipStatus || 'N',
    };

    // Only include optional fields when non-empty
    if (checkInData.email) payload.email = checkInData.email;
    if (checkInData.phone) payload.phone = checkInData.phone;
    if (checkInData.reservationId || checkInData.guestId) {
      payload.reservationId = checkInData.reservationId || checkInData.guestId;
    }

    //  REMOVED: maxUplink, maxDownlink, maxClients parameters

    // Support custom code mode
    if (checkInData.useCustomCode && checkInData.customCode) {
      if (!checkInData.customCode || !checkInData.customCode.trim()) {
        throw new Error('Custom code is required');
      }

      const guestName = (checkInData.guestName || '').trim();
      const fallbackGuestName = guestName || `Guest ${checkInData.roomNumber || ''}`.trim() || 'Guest';

      payload.guestName = fallbackGuestName;
      payload.customCode = checkInData.customCode.trim();
      payload.useCustomCode = true;
      const nameParts = fallbackGuestName.split(' ').filter(Boolean);
      payload.firstName = nameParts[0] || '';
      payload.lastName = nameParts.slice(1).join(' ') || String(checkInData.roomNumber || '').trim() || 'Guest';
    } else {
      const nameParts = (checkInData.guestName || '').trim().split(' ');
      payload.firstName = nameParts[0] || '';
      payload.lastName = nameParts.slice(1).join(' ') || nameParts[0] || 'Guest';
      if (checkInData.guestName?.trim()) {
        payload.guestName = checkInData.guestName.trim();
      }
    }

    const response = await apiClient.post('/rooms/checkin', payload);
    return response.data;
  } catch (error) {
    console.error('Check-in error:', error.response?.data || error.message);
    throw error;
  }
};

// ============================================
// EXPORT ALL APIs
// ============================================
export default {
  auth: authAPI,
  rooms: roomsAPI,
  devices: devicesAPI,
  vouchers: vouchersAPI,
  users: usersAPI,
  branches: branchesAPI,
  dashboard: dashboardAPI,
  settings: settingsAPI,
  logs: logsAPI,
  fiasMonitor: fiasMonitorAPI,
  ruijie: ruijieAPI,
  reports: reportsAPI,
  notifications: notificationsAPI,
  accessTokens: accessTokensAPI,
  templates: templatesAPI,
  files: fileAPI,
  monitoring: monitoringAPI,
  privacy: privacyAPI,
};

// ============================================
// HELPER FUNCTIONS & UTILITIES
// ============================================
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
  }, 100);
};

export const formatErrorMessage = (error) => {
  if (error.isNetworkError) return error.message;
  if (error.response?.data?.message) return error.response.data.message;
  if (error.response?.data?.error) return error.response.data.error;
  if (error.message) return error.message;
  return 'An unexpected error occurred. Please try again.';
};

export const isAuthError = (error) => {
  return (
    error.response?.status === HTTP_STATUS.UNAUTHORIZED ||
    error.type === ERROR_TYPES.AUTHENTICATION
  );
};

export const isPermissionError = (error) => {
  return (
    error.response?.status === HTTP_STATUS.FORBIDDEN ||
    error.type === ERROR_TYPES.AUTHORIZATION
  );
};

export const isValidationError = (error) => {
  return (
    error.response?.status === HTTP_STATUS.UNPROCESSABLE_ENTITY ||
    error.response?.status === HTTP_STATUS.BAD_REQUEST ||
    error.type === ERROR_TYPES.VALIDATION
  );
};

export const getValidationErrors = (error) => {
  if (error.response?.data?.errors) return error.response.data.errors;
  if (error.errors) return error.errors;
  return [];
};

export const buildQueryString = (params) => {
  const query = new URLSearchParams();
  Object.keys(params).forEach((key) => {
    if (params[key] !== null && params[key] !== undefined) {
      query.append(key, params[key]);
    }
  });
  return query.toString();
};

export const retryRequest = async (requestFn, retries = MAX_RETRY_ATTEMPTS) => {
  try {
    return await requestFn();
  } catch (error) {
    if (retries > 0 && (!error.response || error.response.status >= 500)) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      return retryRequest(requestFn, retries - 1);
    }
    throw error;
  }
};

export const checkAPIHealth = async () => {
  try {
    await apiClient.get('/health');
    return true;
  } catch (error) {
    return false;
  }
};

export const getAPIBaseURL = () => {
  return API_URL;
};

export { HTTP_STATUS, ERROR_TYPES, API_TIMEOUT, MAX_RETRY_ATTEMPTS };

console.log(' API.JS - VERSION 13.1.0 - UNIFIED AUDIT LOGS + WIFI PASSWORD VIEW');
console.log(' Now using /logs/audit/unified endpoint with pre-formatted data');
console.log(' Logs display: Room 101 Check-In (John Doe, REG123)');
console.log(' Added getWiFiCredentials() for password view audit logging');
